import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, BarChart3, Briefcase, Loader2, RefreshCw, Server, ShieldX } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const BAND_LABELS = {
  gotowa: "GOTOWA", w_trakcie: "W TRAKCIE",
  ograniczona: "OGRANICZONA INFORMACJA", brak_danych: "BRAK DANYCH",
  nie_wykryto: "NIE WYKRYTO PLANU", zrodlo_niedostepne: "ŹRÓDŁO NIEDOSTĘPNE",
};
const BAND_COLORS = {
  gotowa: "#16A34A", w_trakcie: "#D97706", ograniczona: "#0284C7", brak_danych: "#CBD5E1",
  nie_wykryto: "#64748B", zrodlo_niedostepne: "#CBD5E1",
};
const SOURCE_LABELS = {
  linkedin: "LinkedIn", pink: "PINK", other_social: "Inne social media",
  direct: "Bezpośredni", search: "Wyszukiwarki", other: "Inne",
};
const CONV_STAGES = [
  ["visit", "Sesje surowe (cały ruch, z automatami)"], ["search", "Szukali działki"], ["parcel_view", "Wyświetlili działkę"],
  ["preview", "Podgląd raportu"], ["login_prompt", "Zachęta do logowania"], ["register", "Rejestracja"],
  ["save", "Zapis działki"], ["watch", "Planning Watch"],
];
const INST_ORG_LABELS = {
  samorzad: "Samorząd", deweloper: "Deweloper", kancelaria: "Kancelaria",
  posrednik: "Pośrednik", inwestor: "Inwestor", urbanista: "Urbanista", inne: "Inne",
};
const CTA_STAGE_LABELS = {
  report_preview: "Podgląd raportu",
  save_cta_view: "CTA „Zapisz działkę” — wyświetlenia",
  save_cta_click: "CTA „Zapisz działkę” — kliknięcia",
  reg_start: "Start rejestracji z CTA",
  reg_complete: "Ukończone rejestracje z CTA",
  post_reg_return: "Powrót do działki po rejestracji",
  post_reg_save: "Zapis działki po rejestracji",
  watch_cta_view: "CTA Planning Watch — wyświetlenia",
  watch_cta_click: "CTA Planning Watch — kliknięcia",
  watch_activated: "Aktywacje Planning Watch",
};
const FUNNEL_STEPS = [
  ["visit", "Sesje surowe (cały ruch, z automatami)"], ["search", "Wyszukiwania działek"], ["parcel_view", "Odsłony działek"],
  ["login_prompt", "Zachęty do logowania"], ["register", "Rejestracje"],
  ["parcel_save", "Zapisane działki"], ["watch_start", "Planning Watch"],
];
const KPIS = [
  ["sessions_started", "Wizyty / sesje rozpoczęte"], ["unique_sessions", "Unikalne sesje aktywne"], ["searches", "Wyszukiwania"],
  ["parcel_views", "Odsłony działek"], ["index_views", "Odsłony Indeksu"], ["report_previews", "Podglądy raportów"],
  ["reports", "Pełne raporty"], ["registrations", "Rejestracje"], ["saved_parcels", "Zapisane działki"],
  ["watch_starts", "Planning Watch"], ["demo_views", "Demo PINK (wejścia)"], ["demo_completions", "Demo PINK (ukończenia)"],
];

const isoDay = (d) => d.toISOString().slice(0, 10);
const fmtTs = (iso) =>
  iso ? new Date(iso).toLocaleString("pl-PL", { timeZone: "Europe/Warsaw", hour12: false }) : "—";
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) + "%" : "—");

const PRESETS = [
  ["today", "Dziś"], ["7d", "7 dni"], ["30d", "30 dni"],
];

function presetRange(key) {
  const to = new Date();
  const days = key === "today" ? 0 : key === "7d" ? 6 : 29;
  return { from: isoDay(new Date(Date.now() - days * 864e5)), to: isoDay(to) };
}

function Section({ title, testId, children }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      <h2 className="font-heading text-base font-bold tracking-tight text-slate-900 sm:text-lg">{title}</h2>
      {children}
    </section>
  );
}

function HealthDot({ label, probe, testId }) {
  if (!probe) return null;
  const ok = probe.ok;
  return (
    <div data-testid={testId} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
      <span className="text-sm text-slate-700">{label}</span>
      <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${ok ? "text-emerald-600" : "text-red-600"}`}>
        <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
        {ok ? `OK · ${probe.ms} ms` : (probe.error || `HTTP ${probe.status}`)}
      </span>
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(() => presetRange("7d"));
  const [state, setState] = useState({ data: null, error: null, loading: true });

  const load = useCallback((r) => {
    setState({ data: null, error: null, loading: true });
    api.get("/admin/analytics", { params: r })
      .then((res) => setState({ data: res.data, error: null, loading: false }))
      .catch((e) => setState({ data: null, error: e, loading: false }));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load(range);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (key) => {
    setPreset(key);
    const r = presetRange(key);
    setRange(r);
    load(r);
  };

  const { data, error, loading } = state;

  return (
    <div data-testid="admin-analytics-page" className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <Helmet>
        <title>Analityka — PlanGmina (admin)</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            <BarChart3 className="text-[#DC143C]" size={26} /> Analityka PlanGmina
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Wyłącznie zagregowane liczniki — bez historii użytkowników, adresów IP i surowych zapytań.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="admin-business-link" variant="outline" size="sm" onClick={() => navigate("/admin/business")}>
            <Briefcase size={14} className="mr-1.5" /> Gotowość biznesowa
          </Button>
          <Button data-testid="admin-refresh" variant="outline" size="sm" onClick={() => load(range)} disabled={loading}>
            <RefreshCw size={14} className={loading ? "mr-1.5 animate-spin" : "mr-1.5"} /> Odśwież
          </Button>
        </div>
      </header>

      {!user && (
        <div data-testid="admin-analytics-login-gate" className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-8 text-center">
          <p className="text-sm text-slate-600">Ta strona wymaga konta administratora.</p>
          <Button data-testid="admin-analytics-login-btn" className="mt-4 bg-[#DC143C] text-white hover:bg-[#B91C1C]" onClick={() => navigate("/logowanie?returnTo=/admin/analytics")}>
            Zaloguj
          </Button>
        </div>
      )}
      {user && user.role !== "admin" && (
        <div data-testid="admin-analytics-forbidden" className="mt-8 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
          <ShieldX className="text-red-500" size={20} />
          <p className="text-sm font-medium text-red-700">Brak dostępu — panel dostępny wyłącznie dla administratora po MFA.</p>
        </div>
      )}

      {user?.role === "admin" && (
        <>
          <div data-testid="admin-range-filter" className="mt-6 flex flex-wrap items-center gap-2">
            {PRESETS.map(([key, label]) => (
              <button
                key={key}
                data-testid={`admin-range-${key}`}
                onClick={() => applyPreset(key)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${preset === key ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                {label}
              </button>
            ))}
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <input data-testid="admin-custom-from" type="date" value={range.from}
                onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-xs" />
              <span>–</span>
              <input data-testid="admin-custom-to" type="date" value={range.to}
                onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-xs" />
              <Button data-testid="admin-custom-apply" variant="outline" size="sm" onClick={() => load(range)}>Zastosuj</Button>
            </div>
            <span data-testid="admin-last-event" className="ml-auto text-xs text-slate-400">
              Ostatnie zdarzenie: {fmtTs(data?.last_event_at)}
            </span>
            <span data-testid="admin-timezone" className="text-xs text-slate-400">Strefa czasowa: Europe/Warsaw</span>
          </div>

          <div data-testid="admin-analytics-annotation-2026-09-19" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            <strong>Adnotacja danych (2026-09-19):</strong> 6 z 8 zarejestrowanych zdarzeń rejestracji pochodziło z kontrolowanych testów produkcyjnych. 2 rejestracje były rzeczywiste. Wyłączanie autoryzowanego ruchu testowego z KPI biznesowych obowiązuje od wdrożenia 2026-09-19. Pierwotne liczniki pozostają niezmienione — historia analityki nie jest nadpisywana.
          </div>

          {loading && (
            <div data-testid="admin-analytics-loading" className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
              <Loader2 size={18} className="animate-spin" /> Ładowanie zagregowanych danych…
            </div>
          )}
          {error && (
            <div data-testid="admin-analytics-error" className="mt-6 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
              <AlertTriangle className="text-red-500" size={20} />
              <p className="text-sm text-red-700">{formatApiError(error)}</p>
            </div>
          )}

          {data && (
            <div className="mt-6 space-y-5">
              <Section title={`KPI — ${data.range.from} → ${data.range.to}`} testId="admin-analytics-kpis">
                <div data-testid="admin-kpi-business-strip" className="mt-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">KPI biznesowe — ruch ludzki</p>
                  <div className="mt-2 flex flex-wrap items-end gap-x-8 gap-y-3">
                    <div>
                      <p className="font-heading text-4xl font-extrabold text-emerald-900" data-testid="admin-kpi-human-sessions">{(data.traffic?.qualified_sessions ?? 0).toLocaleString("pl-PL")}</p>
                      <p className="text-xs text-emerald-800">sesje ludzkie (kwalifikowane)</p>
                    </div>
                    <div>
                      <p className="font-heading text-2xl font-extrabold text-slate-900" data-testid="admin-kpi-engaged-sessions">{(data.traffic?.engaged_sessions ?? 0).toLocaleString("pl-PL")}</p>
                      <p className="text-xs text-slate-500">zaangażowane w produkt</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>Surowe: <strong data-testid="admin-kpi-raw-sessions">{(data.traffic?.started_sessions ?? 0).toLocaleString("pl-PL")}</strong></span>
                      <span>Automatyczne: <strong data-testid="admin-kpi-automated-sessions">{(["bot_known", "search_crawler", "link_preview", "security_scanner", "monitoring_probe"].reduce((s, c) => s + (data.traffic?.by_class?.[c] ?? 0), 0)).toLocaleString("pl-PL")}</strong></span>
                      <span>Nieznane: <strong data-testid="admin-kpi-unknown-sessions">{((data.traffic?.by_class?.unknown_technical ?? 0) + (data.traffic?.by_class?.unclassified ?? 0)).toLocaleString("pl-PL")}</strong></span>
                      <span>Testowe: <strong data-testid="admin-kpi-test-sessions">{(data.traffic?.test_sessions ?? 0).toLocaleString("pl-PL")}</strong></span>
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">Surowe liczniki obejmują cały ruch (boty, roboty, podglądy linków, sondy) i pozostają warstwą forenzyjną — nie są nadpisywane. KPI biznesowe to warstwa ludzka/produktowa.</p>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {KPIS.map(([key, label]) => (
                    <div key={key} data-testid={`admin-kpi-${key.replace(/_/g, "-")}`} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                      <p className="mt-1 font-heading text-2xl font-extrabold text-slate-900">
                        {(data.totals[key] ?? 0).toLocaleString("pl-PL")}
                      </p>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Kampania PINK" testId="admin-pink-card">
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {[
                    ["Wejścia w demo", data.pink.visits],
                    ["Unikalne sesje (zbliżone)", data.pink.unique_sessions],
                    ["Ukończenia", data.pink.completions],
                    ["Kliknięcia CTA", data.pink.cta_clicks],
                    ["Rejestracje z kampanii", data.pink.registrations],
                    ["Zapisane działki", data.pink.saved_parcels],
                  ].map(([label, v]) => (
                    <div key={label} className="rounded-xl border border-[#DC143C]/15 bg-[#FEF2F2]/50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                      <p className="mt-1 font-heading text-2xl font-extrabold text-[#DC143C]">{(v ?? 0).toLocaleString("pl-PL")}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-600">
                  <p>Konwersja wejście → ukończenie: <strong data-testid="admin-pink-completion-pct">{pct(data.pink.completions, data.pink.visits)}</strong></p>
                  <p>Konwersja wejście → rejestracja: <strong data-testid="admin-pink-registration-pct">{pct(data.pink.registrations, data.pink.visits)}</strong></p>
                  <p>Kliknięcia „Umów pilotaż”: <strong data-testid="admin-pink-pilot-clicks">{data.pink.pilot_clicks ?? 0}</strong></p>
                  <p>Ostatnia aktywność kampanii: <strong data-testid="admin-pink-last-activity" className="font-mono-data text-xs">{fmtTs(data.pink.last_event_at)}</strong></p>
                </div>
                <p className="mt-3 text-[11px] text-slate-400">
                  Unikalne sesje liczone dokładnie od 11.09.2026 — wcześniejszych nie da się odtworzyć (surowe anonimowe ID wygasają po 24 h i nigdy nie są łączone z kontami).
                </p>
              </Section>

              <Section title="Jakość ruchu — Traffic Integrity" testId="admin-traffic-integrity">
                <p className="mt-1 text-xs text-slate-400">
                  Model trzech warstw: Sesje surowe → Sesje ludzkie (kwalifikowane) → Sesje zaangażowane w produkt.
                  Surowe liczniki obejmują cały ruch (w tym boty, roboty wyszukiwarek, podglądy linków i sondy).
                  Sesje ludzkie wymagają klasy human_likely oraz sygnału zaangażowania przeglądarki po załadowaniu
                  (≥5 s widoczności, scroll, kliknięcie lub akcja produktowa). Sesja zaangażowana w produkt to sesja
                  ludzka z co najmniej jedną sensowną akcją produktową (szukanie lub podgląd działki, podgląd raportu,
                  rejestracja, zapis, Planning Watch, otwarcie CTA instytucjonalnego, instalacja PWA).
                  Pomiar dokładny od {data.traffic?.measurement_start} (zaangażowane: od {data.traffic?.engaged_measurement_start}).
                  Sesja rozpoczęta = bezpośredni ping wejścia lub wejście odtworzone z pierwszej wiarygodnie odebranej aktywności.
                  Sesja aktywna = unikalna sesja, z której odebrano co najmniej jedno zdarzenie.
                  Pomiar wg nowej metodologii od {data.traffic?.started_measurement_start} (w tym wejść odtworzonych: {data.traffic?.inferred_arrivals ?? 0}).
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[
                    ["Sesje rozpoczęte (raw)", data.traffic?.started_sessions, "admin-traffic-raw-visits"],
                    ["Sesje aktywne (raw)", data.traffic?.raw_sessions, "admin-traffic-raw-sessions"],
                    ["Sesje ludzkie (kwalifikowane)", data.traffic?.qualified_sessions, "admin-traffic-qualified"],
                    ["Wskaźnik kwalifikacji",
                     data.traffic?.qualification_rate != null ? `${Math.round(data.traffic.qualification_rate * 100)}%` : "—",
                     "admin-traffic-rate"],
                    ["Sesje zaangażowane w produkt", data.traffic?.engaged_sessions, "admin-traffic-engaged"],
                    ["Wskaźnik zaangażowania",
                     data.traffic?.engagement_rate != null ? `${Math.round(data.traffic.engagement_rate * 100)}%` : "—",
                     "admin-traffic-engaged-rate"],
                  ].map(([label, v, tid]) => (
                    <div key={tid} data-testid={tid} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                      <p className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{(v ?? 0).toLocaleString("pl-PL")}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="admin-traffic-classes">
                  {[
                    ["human_likely", "Prawdopodobnie ludzie"], ["bot_known", "Boty / headless"],
                    ["search_crawler", "Roboty wyszukiwarek"], ["link_preview", "Podglądy linków"],
                    ["security_scanner", "Skanery bezpieczeństwa"], ["monitoring_probe", "Sondy monitoringu"],
                    ["unknown_technical", "Techniczny nieznany"], ["unclassified", "Niesklasyfikowany"],
                  ].map(([key, label]) => (
                    <div key={key} data-testid={`admin-traffic-class-${key.replace(/_/g, "-")}`}
                         className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                      <span className="text-xs text-slate-600">{label}</span>
                      <span className="text-sm font-bold text-slate-900">{(data.traffic?.by_class?.[key] ?? 0).toLocaleString("pl-PL")}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-500" data-testid="admin-traffic-qualified-src">
                  Sesje ludzkie wg źródła:{" "}
                  {Object.entries(data.traffic?.qualified_by_source || {}).map(([c, n]) =>
                    `${SOURCE_LABELS[c] || c}: ${n}`).join(" · ") || "—"}
                </p>
                <p className="mt-1 text-xs text-slate-500" data-testid="admin-traffic-engaged-src">
                  Sesje zaangażowane wg źródła:{" "}
                  {Object.entries(data.traffic?.engaged_by_source || {}).map(([c, n]) =>
                    `${SOURCE_LABELS[c] || c}: ${n}`).join(" · ") || "—"}
                </p>
              </Section>

              <Section title="Aktywność — liczba zdarzeń (NIE unikalni użytkownicy)" testId="admin-funnel">
                <p className="mt-1 text-xs text-slate-400">
                  Liczby zdarzeń mogą przekraczać liczbę wizyt — to aktywność, nie konwersja użytkowników. Konwersję pokazuje wyłącznie lejek unikalnych sesji poniżej.
                </p>
                <div className="mt-4 space-y-2">
                  {data.funnel.map((f, i) => {
                    const max = data.funnel[0]?.count || 1;
                    return (
                      <div key={f.key} data-testid={`admin-funnel-${f.key}`} className="flex items-center gap-3">
                        <span className="w-44 shrink-0 text-xs font-medium text-slate-600">{f.label}</span>
                        <div className="h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                          <div className="h-full rounded-md bg-[#DC143C]/80 transition-[width] duration-500"
                            style={{ width: `${Math.max((f.count / max) * 100, f.count ? 2 : 0)}%` }} />
                        </div>
                        <span className="w-16 text-right font-mono-data text-xs font-bold text-slate-900">{f.count.toLocaleString("pl-PL")}</span>
                        <span className="w-12 text-right text-xs text-slate-400">{i === 0 ? "" : pct(f.count, data.funnel[i - 1].count)}</span>
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section title="Kwalifikowane użycie produktu — sesje czyste" testId="admin-qualified-funnel">
                <p className="mt-1 text-xs text-slate-400">
                  Wyłącznie sesje kwalifikowane (klasa human_likely + sygnał zaangażowania). To jest lejek wiarygodny dla partnerów/M&amp;A.
                </p>
                <div className="mt-4 space-y-2">
                  {[
                    ["qualified", "Sesje ludzkie (kwalifikowane)", data.traffic?.qualified_sessions ?? 0],
                    ["engaged", "Sesje zaangażowane w produkt", data.traffic?.engaged_sessions ?? 0],
                    ["search", "Szukali działki", data.traffic?.qfunnel?.search ?? 0],
                    ["parcel_view", "Wyświetlili działkę", data.traffic?.qfunnel?.parcel_view ?? 0],
                    ["preview", "Otworzyli podgląd raportu", data.traffic?.qfunnel?.preview ?? 0],
                    ["register", "Zarejestrowali się", data.traffic?.qfunnel?.register ?? 0],
                    ["save", "Zapisali działkę", data.traffic?.qfunnel?.save ?? 0],
                    ["watch", "Włączyli Planning Watch", data.traffic?.qfunnel?.watch ?? 0],
                  ].map(([k, l, c], i, arr) => (
                    <div key={k} data-testid={`admin-qfunnel-${k}`} className="flex items-center gap-3">
                      <span className="w-48 text-xs text-slate-600">{l}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded bg-slate-100">
                        <div className="h-full bg-emerald-500" style={{ width: `${Math.max(2, (c / (arr[0][2] || 1)) * 100)}%` }} />
                      </div>
                      <span className="w-12 text-right text-xs font-bold text-slate-900">{c}</span>
                      <span className="w-12 text-right text-xs text-slate-400">{i === 0 ? "" : pct(c, arr[i - 1][2])}</span>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Jakość wyszukiwarki — resolver (7 dni)" testId="admin-resolver">
                {data.resolver ? (
                  <>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {[
                        ["Wyszukiwania", data.resolver.metrics?.total_searches ?? 0, "admin-resolver-total"],
                        ["Bez wyniku", `${data.resolver.metrics?.no_result ?? 0} (${data.resolver.metrics?.no_result_rate ?? "—"}%)`, "admin-resolver-noresult"],
                        ["Normalizacja city-first",
                         data.resolver.metrics?.fallback_used
                           ? `${data.resolver.metrics.fallback_success}/${data.resolver.metrics.fallback_used} uratowane`
                           : "—", "admin-resolver-fallback"],
                        ["Później rozwiązane",
                         data.resolver.metrics?.later_resolved_rate != null
                           ? `${data.resolver.metrics.later_resolved_rate}%` : "—", "admin-resolver-resolved"],
                      ].map(([l, v, id]) => (
                        <div key={id} data-testid={id} className="rounded-xl bg-slate-50 px-3 py-2">
                          <p className="text-xs text-slate-500">{l}</p>
                          <p className="font-heading text-lg font-bold text-slate-900">{v}</p>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-slate-500" data-testid="admin-resolver-patterns">
                      Najczęstsze wzorce porażek:{" "}
                      {(data.resolver.metrics?.top_patterns || []).slice(0, 3)
                        .map((p) => `„${p.pattern}” ×${p.count}`).join(" · ") || "brak"}
                    </p>
                    <p className="mt-1 text-xs text-slate-400" data-testid="admin-resolver-meta">
                      Tydzień {data.resolver.week_start} → {data.resolver.week_end} · tyg./tyg.:{" "}
                      {data.resolver.metrics?.wow_delta_pp != null
                        ? `${data.resolver.metrics.wow_delta_pp > 0 ? "+" : ""}${data.resolver.metrics.wow_delta_pp} p.p.`
                        : "—"}{" "}
                      · surowe zapytania nigdy nie są zapisywane; znormalizowane wzorce z TTL 14 dni.
                    </p>
                  </>
                ) : (
                  <p className="mt-2 text-xs text-slate-500" data-testid="admin-resolver-empty">
                    Pierwszy digest resolvera zostanie wygenerowany w poniedziałek o 08:00.
                  </p>
                )}
              </Section>

              <Section title="Lejek konwersji — unikalne sesje (SUROWE — ruch niezweryfikowany)" testId="admin-session-funnel">
                <p className="mt-1 text-xs text-slate-400">
                  Unikalne sesje, które dotarły do danego etapu (prawdziwe proporcje konwersji; dokładne od 11.09.2026).
                </p>
                <div className="mt-4 space-y-2">
                  {data.session_funnel.map((f, i) => {
                    const max = data.session_funnel[0]?.sessions || 1;
                    return (
                      <div key={f.stage} data-testid={`admin-sfunnel-${f.stage}`} className="flex items-center gap-3">
                        <span className="w-44 shrink-0 text-xs font-medium text-slate-600">{f.label}</span>
                        <div className="h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                          <div className="h-full rounded-md bg-slate-700 transition-[width] duration-500"
                            style={{ width: `${Math.max((f.sessions / max) * 100, f.sessions ? 2 : 0)}%` }} />
                        </div>
                        <span className="w-16 text-right font-mono-data text-xs font-bold text-slate-900">{f.sessions.toLocaleString("pl-PL")}</span>
                        <span className="w-12 text-right text-xs text-slate-400">{i === 0 ? "" : pct(f.sessions, data.session_funnel[i - 1].sessions)}</span>
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section title="Kampanie (parametr ref)" testId="admin-campaigns">
                <div className="mt-4 overflow-x-auto">
                  <table data-testid="admin-campaigns-table" className="w-full min-w-[640px] text-left text-xs">
                    <thead>
                      <tr className="bg-slate-900 text-white">
                        <th className="px-3 py-2">Kampania</th><th className="px-3 py-2">Wizyty</th>
                        <th className="px-3 py-2">Starty demo</th><th className="px-3 py-2">Ukończenia</th>
                        <th className="px-3 py-2">Kliknięcia CTA</th><th className="px-3 py-2">Rejestracje</th>
                        <th className="px-3 py-2">Zapisane działki</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.campaigns.map((c) => (
                        <tr key={c.ref} data-testid={`admin-campaign-${c.ref}`} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-2 font-mono-data font-bold text-slate-900">{c.ref}</td>
                          <td className="px-3 py-2">{c.visits}</td><td className="px-3 py-2">{c.demo_views}</td>
                          <td className="px-3 py-2">{c.demo_completions}</td><td className="px-3 py-2">{c.cta_clicks}</td>
                          <td className="px-3 py-2">{c.registrations}</td><td className="px-3 py-2">{c.saved_parcels}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="Kampania EDU — uczelnie (kody instytucji, bez danych osobowych)" testId="admin-edu-campaigns">
                <p className="mt-1 text-xs text-slate-400">
                  Wyłącznie zagregowane kody kampanii/instytucji — bez identyfikatorów osób, bez fingerprintingu.
                </p>
                {(data.edu_campaigns || []).length === 0 ? (
                  <p className="mt-4 text-xs text-slate-400" data-testid="admin-edu-campaigns-empty">Brak ruchu z kampanii EDU.</p>
                ) : (
                  <>
                    {/* Mobile: stacked cards, one per institution code (no clipped columns) */}
                    <div className="mt-4 space-y-3 md:hidden" data-testid="admin-edu-campaigns-cards">
                      {data.edu_campaigns.map((c) => (
                        <div key={c.ref} data-testid={`admin-edu-campaign-card-${c.ref}`}
                          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          <p className="font-mono-data text-sm font-bold text-slate-900">{c.ref}</p>
                          <dl className="mt-3 space-y-1.5 text-xs">
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-slate-500">Pierwsza aktywność</dt>
                              <dd className="font-medium text-slate-700">{(c.first_at || "").slice(0, 16).replace("T", " ")}</dd>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-slate-500">Wizyty</dt>
                              <dd className="font-heading text-base font-extrabold text-slate-900">{c.visits || 0}</dd>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-slate-500">Kliknięcia CTA</dt>
                              <dd className="font-heading text-base font-extrabold text-slate-900">{c.cta_clicks || 0}</dd>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <dt className="text-slate-500">Zapytania o pilotaż</dt>
                              <dd className="font-heading text-base font-extrabold text-red-700">{c.inquiries || 0}</dd>
                            </div>
                          </dl>
                        </div>
                      ))}
                    </div>
                    {/* Desktop/tablet: full table */}
                    <div className="mt-4 hidden overflow-x-auto md:block">
                    <table data-testid="admin-edu-campaigns-table" className="w-full min-w-[560px] text-left text-xs">
                      <thead>
                        <tr className="bg-slate-900 text-white">
                          <th className="px-3 py-2">Kod instytucji</th><th className="px-3 py-2">Pierwsza aktywność</th>
                          <th className="px-3 py-2">Wizyty</th><th className="px-3 py-2">Kliknięcia CTA</th>
                          <th className="px-3 py-2">Zapytania o pilotaż</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.edu_campaigns.map((c) => (
                          <tr key={c.ref} data-testid={`admin-edu-campaign-${c.ref}`} className="border-b border-slate-100 last:border-0">
                            <td className="px-3 py-2 font-mono-data font-bold text-slate-900">{c.ref}</td>
                            <td className="px-3 py-2">{(c.first_at || "").slice(0, 16).replace("T", " ")}</td>
                            <td className="px-3 py-2">{c.visits || 0}</td>
                            <td className="px-3 py-2">{c.cta_clicks || 0}</td>
                            <td className="px-3 py-2">{c.inquiries || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </>
                )}
              </Section>

              <Section title="Źródła ruchu (kategorie zagregowane)" testId="admin-sources">
                <p className="mt-1 text-xs text-slate-400">
                  Wyłącznie kategoria źródła — surowe adresy URL stron odsyłających nigdy nie są zapisywane.
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {Object.entries(SOURCE_LABELS).map(([key, label]) => {
                    const n = data.sources?.by_category?.[key] ?? 0;
                    return (
                      <div key={key} data-testid={`admin-source-${key}`} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                        <p className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{n.toLocaleString("pl-PL")}</p>
                        <p className="text-[11px] text-slate-400">{pct(n, data.sources?.total || 0)}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Najsilniejsze źródło: <strong data-testid="admin-source-top">{data.sources?.top ? SOURCE_LABELS[data.sources.top] : "—"}</strong>
                </p>
                <p data-testid="admin-source-session-grain" className="mt-2 text-[11px] text-slate-400">
                  Ziarno sesji (od {data.sources?.session_grain_start}): {data.sources?.session_total ?? 0} aktywnych = {Object.values(data.sources?.session_grained || {}).reduce((a, b) => a + b, 0)} przypisanych + {data.sources?.session_unattributed ?? 0} bez atrybucji / starsze. Powyższe kafelki zachowują ziarno zdarzeń (diagnostyka).
                </p>
              </Section>

              <Section title="Konwersja wg źródła ruchu (unikalne sesje)" testId="admin-conv-by-source">
                <p className="mt-1 text-xs text-slate-400">
                  Sesje zachowują kategorię źródła od wejścia do konwersji; sesje bez znanej kategorii nie są przypisywane. Surowe adresy URL nigdy nie są zapisywane.
                </p>
                <div className="mt-4 overflow-x-auto">
                  <table data-testid="admin-conv-source-table" className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-400">
                        <th className="py-1.5 pr-3 font-semibold">Etap</th>
                        {Object.values(SOURCE_LABELS).map((l) => (
                          <th key={l} className="px-2 py-1.5 text-right font-semibold">{l}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {CONV_STAGES.map(([stage, label]) => (
                        <tr key={stage} data-testid={`admin-conv-src-${stage}`}>
                          <td className="py-1.5 pr-3 font-medium text-slate-600">{label}</td>
                          {Object.keys(SOURCE_LABELS).map((c) => (
                            <td key={c} className="px-2 py-1.5 text-right font-mono-data text-slate-900">
                              {(data.conversion_by_source?.[stage]?.[c] ?? 0).toLocaleString("pl-PL")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="Ruch instytucjonalny" testId="admin-institutional">
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Wyświetlenia CTA</p>
                    <p data-testid="admin-inst-cta-views" className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{(data.institutional?.cta_views ?? 0).toLocaleString("pl-PL")}</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Prośby o prezentację</p>
                    <p data-testid="admin-inst-requests" className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{(data.institutional?.requests ?? 0).toLocaleString("pl-PL")}</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Konwersja CTA → prośba</p>
                    <p data-testid="admin-inst-conv" className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{pct(data.institutional?.requests ?? 0, data.institutional?.cta_views ?? 0)}</p>
                  </div>
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Typ organizacji</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(INST_ORG_LABELS).map(([k, l]) => (
                    <span key={k} data-testid={`admin-inst-org-${k}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                      {l}: <strong>{(data.institutional?.by_org_type?.[k] ?? 0).toLocaleString("pl-PL")}</strong>
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Konwersja wg źródła</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(SOURCE_LABELS).map(([k, l]) => (
                    <span key={k} data-testid={`admin-inst-src-${k}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                      {l}: <strong>{(data.institutional?.by_source?.[k] ?? 0).toLocaleString("pl-PL")}</strong>
                    </span>
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-slate-400">
                  Wyłącznie dobrowolne zgłoszenia ze znacznikiem zgody — bez fingerprintingu, wzbogacania IP ani deanonimizacji.
                </p>
              </Section>

              <Section title="Lejek aktywacji CTA — zdarzenia vs unikalne sesje" testId="admin-cta-funnel">
                <p className="mt-1 text-xs text-slate-400">
                  Kolumny są rozłączne: zdarzenia liczą każdą akcję, sesje — raz na sesję dziennie. Konwersja liczona wyłącznie na sesjach; przy zerowym mianowniku pokazujemy —.
                </p>
                <div className="mt-4 overflow-x-auto">
                  <table data-testid="admin-cta-funnel-table" className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-400">
                        <th className="py-1.5 pr-3 font-semibold">Etap</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Zdarzenia</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Unikalne sesje</th>
                        <th className="px-2 py-1.5 text-right font-semibold">Konwersja (sesje)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(data.cta_funnel || []).map((row, i, arr) => {
                        const prev = i > 0 ? arr[i - 1].sessions : 0;
                        return (
                          <tr key={row.stage} data-testid={`admin-cta-stage-${row.stage}`}>
                            <td className="py-1.5 pr-3 font-medium text-slate-600">{CTA_STAGE_LABELS[row.stage] || row.stage}</td>
                            <td className="px-2 py-1.5 text-right font-mono-data text-slate-500">{row.events.toLocaleString("pl-PL")}</td>
                            <td className="px-2 py-1.5 text-right font-mono-data font-bold text-slate-900">{row.sessions.toLocaleString("pl-PL")}</td>
                            <td className="px-2 py-1.5 text-right font-mono-data text-slate-900">
                              {i === 0 ? "—" : prev === 0 ? "—" : `${Math.round((row.sessions / prev) * 100)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Section>

              <div className="grid gap-5 lg:grid-cols-2">
                <Section title="Indeks Gotowości Gmin" testId="admin-index-section">
                  <div className="mt-4 flex flex-wrap gap-2">
                    {Object.entries(data.index.bands).map(([b, n]) => (
                      <span key={b} data-testid={`admin-band-${b}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: BAND_COLORS[b] }} />
                        {BAND_LABELS[b]} · {n.toLocaleString("pl-PL")}
                      </span>
                    ))}
                  </div>
                  <p data-testid="admin-index-source-health" className="mt-3 text-xs text-slate-500">
                    Diagnostyka źródeł: wszystkie OK <strong>{data.index.source_health?.all_ok ?? 0}</strong> · częściowo niedostępne <strong>{data.index.source_health?.degraded ?? 0}</strong> · niedostępne <strong>{data.index.source_health?.failed ?? 0}</strong> · bez kontroli <strong>{data.index.source_health?.unchecked ?? 0}</strong>
                  </p>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Najczęściej oglądane gminy</p>
                  <ul className="mt-2 space-y-1">
                    {data.index.top_viewed.length === 0 && <li className="text-xs text-slate-400">Brak danych w tym zakresie</li>}
                    {data.index.top_viewed.map((g) => (
                      <li key={g.id} className="flex justify-between text-xs">
                        <span className="font-medium text-slate-700">{g.name} <span className="font-mono-data text-slate-400">{g.id}</span></span>
                        <span className="font-mono-data font-bold">{g.count}</span>
                      </li>
                    ))}
                  </ul>
                </Section>

                <Section title="Aktywność działek" testId="admin-parcel-section">
                  <div className="mt-4 grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
                    {[
                      ["Wyszukiwania", data.parcel.searches, "admin-parcel-searches"],
                      ["Z wynikami", data.parcel.with_results ?? 0, "admin-parcel-withresults"],
                      ["Bez wyniku", data.parcel.no_result, "admin-parcel-noresult"],
                      ["Odsłony działki (osobno)", data.parcel.parcel_views ?? 0, "admin-parcel-views"],
                    ].map(([label, v, tid]) => (
                      <div key={tid} data-testid={tid} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                        <p className="mt-1 font-heading text-xl font-extrabold text-slate-900">{v.toLocaleString("pl-PL")}</p>
                      </div>
                    ))}
                  </div>
                  <p data-testid="admin-parcel-outcomes-note" className="mt-2 text-[11px] text-slate-400">
                    Wyniki rozłączne od {data.parcel.outcomes_start}: z wynikami + bez wyniku = zakończone wyszukiwania. Odsłony działki to osobna metryka (dowolne źródło wejścia).
                  </p>
                  <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Najczęściej wyszukiwane gminy (agregat TERYT)</p>
                  <ul className="mt-2 space-y-1">
                    {data.parcel.top_gminas.length === 0 && <li className="text-xs text-slate-400">Brak danych w tym zakresie</li>}
                    {data.parcel.top_gminas.map((g) => (
                      <li key={g.id} className="flex justify-between text-xs">
                        <span className="font-medium text-slate-700">{g.name} <span className="font-mono-data text-slate-400">{g.id}</span></span>
                        <span className="font-mono-data font-bold">{g.count}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              </div>

              <Section title="Kondycja operacyjna" testId="admin-health-panel">
                <div className="mt-4 grid gap-2 sm:grid-cols-3">
                  <HealthDot label="GUGiK ULDK" probe={data.health.uldk} testId="admin-health-uldk" />
                  <HealthDot label="Geoportal WMS (MPZP)" probe={data.health.mpzp_wms} testId="admin-health-geoportal-wms-mpzp" />
                  <HealthDot label="Geoportal WMS (Plan Ogólny)" probe={data.health.pog_wms} testId="admin-health-plan-ogolny-wms" />
                </div>
                <div className="mt-4 grid gap-2 text-xs text-slate-600 sm:grid-cols-2 lg:grid-cols-4">
                  <p data-testid="admin-health-harvest">Ostatni harvest gmin: <strong className="font-mono-data">{fmtTs(data.health.last_harvest)}</strong></p>
                  <p data-testid="admin-health-watch">Ostatni Planning Watch: <strong className="font-mono-data">{fmtTs(data.health.last_watch)}</strong></p>
                  <p data-testid="admin-health-adapter-fails">Błędy adapterów: <strong>{data.health.adapter_failures}</strong></p>
                  <p data-testid="admin-health-ratelimits">Żądania zablokowane limitem 429 (anon/auth): <strong>{data.health.rate_limit_anon} / {data.health.rate_limit_auth}</strong></p>
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
                  <Server size={12} /> Sondy na żywo z tego podglądu · <Activity size={12} /> dane źródłowe: liczniki dzienne
                </p>
              </Section>
            </div>
          )}
        </>
      )}
    </div>
  );
}
